import { useEffect, useState } from 'react';
import { Modal, Tag, Spin, Empty, Input } from 'antd';
import { Search as SearchIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { taskService } from '@/services/taskService';
import type { Task as TaskDto } from '@/types';

/**
 * V1.0.35: Modal hiển thị danh sách task — dùng cho drill-down từ:
 *  - Báo cáo Quá hạn theo khoa (click cột chart hoặc số trong bảng)
 *  - Tương lai: các báo cáo khác cần drill-down task
 *
 * Filter có thể combine:
 *  - departmentId: theo khoa
 *  - statusBucket: 'overdue' | 'inProgress' | 'completed' | 'all'
 *  - fromDate / toDate: lọc theo created_at
 *
 * Click task trong popup → navigate sang chi tiết task.
 */
export interface TaskListPopupParams {
  visible: boolean;
  title: string;
  color?: string;
  /** Khoa ID để filter task */
  departmentId?: string;
  /** Bucket trạng thái cần hiển thị */
  statusBucket?: 'all' | 'overdue' | 'inProgress' | 'completed';
  /** Khoảng thời gian (theo created_at task) */
  fromDate?: string; // YYYY-MM-DD
  toDate?: string;
}

interface Props {
  params: TaskListPopupParams | null;
  onClose: () => void;
  isMobile?: boolean;
}

export function TaskListPopupModal({ params, onClose, isMobile }: Props) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!params?.visible) {
      setTasks([]);
      setSearch('');
      return;
    }
    setLoading(true);
    // Gọi taskService.list — endpoint backend filter theo dept + search + date
    const apiParams: any = {
      tab: 'all',
      pageSize: 200,
      departmentId: params.departmentId,
    };
    taskService.list(apiParams)
      .then((resp) => {
        let items = resp.items ?? [];

        // Filter date range client-side (theo createdAt)
        if (params.fromDate) {
          const from = dayjs(params.fromDate);
          items = items.filter(t => !t.createdAt || dayjs(t.createdAt).isAfter(from.subtract(1, 'day')));
        }
        if (params.toDate) {
          const to = dayjs(params.toDate);
          items = items.filter(t => !t.createdAt || dayjs(t.createdAt).isBefore(to.add(1, 'day')));
        }

        // Filter status bucket
        if (params.statusBucket === 'overdue') {
          items = items.filter(t =>
            t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
            && t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), 'day')
          );
        } else if (params.statusBucket === 'inProgress') {
          items = items.filter(t =>
            t.status === 'IN_PROGRESS' || t.status === 'ASSIGNED' || (t.status as any) === 'NEW'
          );
        } else if (params.statusBucket === 'completed') {
          items = items.filter(t => t.status === 'COMPLETED');
        }
        // 'all' = không filter

        setTasks(items);
      })
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [params?.visible, params?.departmentId, params?.statusBucket, params?.fromDate, params?.toDate]);

  // Client-side search
  const filteredTasks = search
    ? tasks.filter(t =>
        t.title?.toLowerCase().includes(search.toLowerCase())
        || t.code?.toLowerCase().includes(search.toLowerCase())
      )
    : tasks;

  return (
    <Modal
      open={!!params?.visible}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {params?.color && (
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: params.color,
            }} />
          )}
          <span style={{ fontSize: 14 }}>{params?.title}</span>
          <Tag color="blue">{filteredTasks.length} task</Tag>
        </div>
      }
      footer={null}
      width={isMobile ? '95vw' : 760}
    >
      <Input
        placeholder="Tìm theo tiêu đề hoặc mã task..."
        prefix={<SearchIcon size={14} style={{ color: '#bdc4cd' }} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
        allowClear
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : filteredTasks.length === 0 ? (
        <Empty description="Không có task nào" />
      ) : (
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {filteredTasks.map(t => {
            const urg = (t as any).priorityLevel ?? 3;
            const imp = (t as any).importantLevel ?? 3;
            const isOverdue = t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), 'day') && t.status !== 'COMPLETED';
            return (
              <div
                key={t.id}
                onClick={() => {
                  navigate(`/tasks/${t.id}`);
                  onClose();
                }}
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, marginBottom: 4,
                      display: 'flex', gap: 8, flexWrap: 'wrap',
                    }}>
                      <span style={{ color: urg >= 4 ? '#cf1322' : urg >= 3 ? '#fa8c16' : '#7d8a98' }}>
                        🔴 Gấp: Cấp {urg}
                      </span>
                      <span style={{ color: '#bdc4cd' }}>·</span>
                      <span style={{ color: imp >= 4 ? '#d46b08' : imp >= 3 ? '#faad14' : '#7d8a98' }}>
                        🟠 Quan trọng: Cấp {imp}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1e23', marginBottom: 4 }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#5b6875', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#1976D2' }}>{t.code}</span>
                      <span>·</span>
                      <Tag style={{ fontSize: 10, padding: '0 6px', lineHeight: '18px', margin: 0 }}>
                        {t.status}
                      </Tag>
                      {t.dueDate && (
                        <>
                          <span>·</span>
                          <span style={{ color: isOverdue ? '#cf1322' : '#5b6875', fontWeight: isOverdue ? 600 : 400 }}>
                            📅 {dayjs(t.dueDate).format('DD/MM/YYYY')}
                            {isOverdue && ' (Quá hạn)'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 16, color: '#1976D2' }}>→</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
